import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  TextField,
  Grid,
  Divider
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAuth } from "../../context/authContext";
import MarkdownViewerModal from "./MarkdownViewerModal";
import PageTitle from "../PageTitle/PageTitle";

/**
 * Parse markdown into:
 * 1. Static content (Overview + Responsibilities)
 * 2. Administrator Responsibilities
 * 3. Sectioned quick links
 */
const parseMarkdown = (markdown) => {
  const lines = markdown.split("\n");

  let welcomeText = "";
  let topContent = [];
  let sections = {};
  let adminResponsibilities = {};

  let currentSection = null;
  let currentAdminSection = null;
  let inQuickLinks = false;
  let inAdminResponsibilities = false;
  let welcomeExtracted = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Extract welcome text (first non-empty, non-header line)
    if (!welcomeExtracted && line.trim() && !line.startsWith("#")) {
      welcomeText = line;
      welcomeExtracted = true;
      continue;
    }
    
    // Handle Administrator Responsibilities section
    if (line.startsWith("## Administrator Responsibilities")) {
      inQuickLinks = false;
      inAdminResponsibilities = true;
      continue;
    }
    
    // Start Quick Links
    if (line.startsWith("## Quick Links Navigation")) {
      inQuickLinks = true;
      inAdminResponsibilities = false;
      continue;
    }

    if (!inQuickLinks && !inAdminResponsibilities) {
      topContent.push(line);
      continue;
    }

    // Parse Admin Responsibilities subsections
    if (inAdminResponsibilities) {
      if (line.startsWith("### ")) {
        currentAdminSection = line.replace("### ", "").trim();
        adminResponsibilities[currentAdminSection] = [];
        continue;
      }

      // Match numbered list items
      const numberMatch = line.match(/^\d+\.\s*\*?\*?([^:]+)\*?\*?:?\s*(.*)/);
      if (numberMatch && currentAdminSection) {
        const title = numberMatch[1].trim().replace(/\*/g, '');
        const description = numberMatch[2].trim();
        adminResponsibilities[currentAdminSection].push({
          title,
          description,
        });
      }
      continue;
    }

    // Parse Quick Links sections
    if (inQuickLinks) {
      if (line.startsWith("### ")) {
        currentSection = line.replace("### ", "").trim();
        sections[currentSection] = [];
        continue;
      }

      if (line.includes("](") && currentSection) {
        const match = line.match(
          /\[([^\]]+)\]\(([^)]+)\)\s*-?\s*(.*)/
        );

        if (!match) continue;

        const [, title, link, description] = match;

        if (link.endsWith(".md")) {
          const nextLine = lines[i + 1];
          const videoMatch = nextLine?.match(/\((.*\.mp4)\)/);

          sections[currentSection].push({
            title,
            description,
            docLink: link,
            videoLink: videoMatch ? videoMatch[1] : null,
          });
        }
      }
    }
  }

  return {
    welcomeText,
    topMarkdown: topContent.join("\n"),
    sections,
    adminResponsibilities,
  };
};


const MarkdownHelpPage = () => {
  const { user } = useAuth();

  const [markdown, setMarkdown] = useState("");
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");


  const isAdminRole =
    user?.role?.role_name === "Admin" ||
    user?.role?.role_type === "Admin" ||
    user?.user_role === "Admin";

  useEffect(() => {
    const filePath = isAdminRole
      ? "/help_guide/overview_administrator_help.md"
      : "/help_guide/overview_customer_help.md";

    setLoading(true);
    fetch(filePath)
      .then((res) => res.text())
      .then(setMarkdown)
      .finally(() => setLoading(false));
  }, [isAdminRole]);

  const { welcomeText, topMarkdown, sections, adminResponsibilities } = useMemo(
    () => parseMarkdown(markdown),
    [markdown]
  );
  const filteredSections = useMemo(() => {
    if (!searchTerm) return sections;

    const lowerSearch = searchTerm.toLowerCase();
    const result = {};

    Object.entries(sections).forEach(([section, items]) => {
      const filteredItems = items.filter(
        (item) =>
          item.title.toLowerCase().includes(lowerSearch) ||
          (item.description &&
            item.description.toLowerCase().includes(lowerSearch)) ||
          section.toLowerCase().includes(lowerSearch)
      );

      if (filteredItems.length > 0) {
        result[section] = filteredItems;
      }
    });

    return result;
  }, [sections, searchTerm]);



  return (
    <Box p={3} maxWidth="1200px" mx="auto">
      <PageTitle title="Help Guide" />

      {loading && (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <>
          {/*  WELCOME TEXT */}
          {welcomeText && (
            <Typography variant="body1" mb={4} color="text.secondary">
              {welcomeText}
            </Typography>
          )}

          {/*  ADMINISTRATOR RESPONSIBILITIES CARDS */}
          {Object.entries(adminResponsibilities).length > 0 && (
            <Box mb={6}>
              <Typography variant="h5" fontWeight={700} mb={3}>
                Administrator Responsibilities
              </Typography>
              <Grid container spacing={3}>
                {Object.entries(adminResponsibilities).map(
                  ([section, items]) => (
                    <Grid item xs={12} sm={6} key={section}>
                      <Box
                        sx={{
                          border: "1px solid #e0e0e0",
                          borderRadius: 2,
                          p: 2,
                          borderBottom:"2px solid #0288d1",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          "&:hover": { boxShadow: 2 },
                        }}
                      >
                        <Box>
                          <Typography
                            variant="h5"
                            fontWeight={700}
                            mb={1}
                            sx={{
                              paddingBottom: 1,
                              borderBottom: "2px solid #0288d1",
                              display: "inline-block",
                            }}
                          >
                            {section}
                          </Typography>

                          <Box mt={2}>
                            {items.map((item, idx) => (
                              <Box key={idx} mb={1.5}>
                                <Typography variant="body2" color="text.primary">
                                  <span style={{ fontWeight: 700 }}>
                                    {item.title}:
                                  </span>
                                  {item.description && (
                                    <span style={{ marginLeft: "4px" }}>
                                      {item.description}
                                    </span>
                                  )}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  )
                )}
              </Grid>
            </Box>
          )}

          {/*  OVERVIEW AT TOP */}
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {topMarkdown}
          </ReactMarkdown>

          <Divider sx={{ my: 4 }} />

          {/*  HELP TOPICS HEADER AND SEARCH */}
          <Typography variant="h5" fontWeight={700} mb={3}>
            Help Topics
          </Typography>
          <Box mb={3}>
            <TextField
              fullWidth
              placeholder="Search help topics..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
            />
          </Box>


          {/*  QUICK LINKS (CARDS) */}
          {/* {Object.entries(sections).map(([section, items]) => ( */}
          {Object.entries(filteredSections).map(([section, items]) => (
            <Box key={section} mt={4}>
              <Typography variant="h6" fontWeight={600} mb={2}>
                {section}
              </Typography>

              <Box display="flex" flexDirection="column" gap={2}>
                {items.map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: 2,
                      p: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      "&:hover": { boxShadow: 2 },
                    }}
                  >

                    <Box>
                      <Typography fontWeight={800}>
                        {item.title}
                      </Typography>

                      {item.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          mt={0.5}
                        >
                          {item.description}
                        </Typography>
                      )}
                    </Box>


                    <Box display="flex" gap={1}>
                      <Button
                        onClick={() => {
                          setSelectedFile(item.docLink);
                          setModalOpen(true);
                        }}
                        sx={{ minWidth: "auto" }}
                      >
                        <DescriptionIcon color="primary" />
                      </Button>

                      {item.videoLink && (
                        <Button
                          onClick={() => {
                            setSelectedFile(item.videoLink);
                            setModalOpen(true);
                          }}
                          sx={{ minWidth: "auto" }}
                        >
                          <PlayCircleOutlineIcon color="success" />
                        </Button>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}




        </>
      )}

      <MarkdownViewerModal
        open={modalOpen}
        file={selectedFile}
        onClose={() => setModalOpen(false)}
      />
    </Box>
  );
};

export default MarkdownHelpPage;